import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { invoiceService } from '../services';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  FileText, Search, Filter, Loader2, Cloud, AlertCircle,
  RefreshCw, Eye, Calendar, Building, Download, FileDown, CreditCard
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};

export const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [source, setSource] = useState(null);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [downloadingEInvoice, setDownloadingEInvoice] = useState(null);
  const [activeTab, setActiveTab] = useState('invoices');

  // Detail dialog
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const [invoiceData, cnData] = await Promise.all([
        invoiceService.getInvoices(params),
        invoiceService.getCreditNotes({ limit: 100 })
      ]);

      if (invoiceData.error) {
        setError(invoiceData.error);
        setInvoices([]);
      } else {
        setInvoices(invoiceData.invoices || []);
        setTotalCount(invoiceData.total || 0);
      }
      setCreditNotes(cnData.creditnotes || []);
      setSource(invoiceData.source || 'unknown');
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Fehler beim Laden der Rechnungen');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const openInvoiceDetail = async (invoice) => {
    setShowDetail(true);
    setLoadingDetail(true);
    try {
      const detail = await invoiceService.getInvoiceDetail(invoice.invoice_id);
      if (detail.found) {
        setInvoiceDetail(detail.invoice);
      } else {
        setInvoiceDetail(invoice);
      }
    } catch (err) {
      console.error('Error fetching invoice detail:', err);
      toast.error('Fehler beim Laden der Rechnungsdetails');
      setInvoiceDetail(invoice);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber, e) => {
    if (e) e.stopPropagation();
    setDownloadingPdf(invoiceId);
    try {
      const response = await invoiceService.downloadPdf(invoiceId);
      if (response.ok) {
        const blob = await response.blob();
        triggerDownload(blob, `Rechnung_${invoiceNumber || invoiceId}.pdf`);
        toast.success('PDF heruntergeladen');
      } else {
        const json = await response.json().catch(() => null);
        toast.error(json?.detail || 'Fehler beim PDF-Download');
      }
    } catch (err) {
      toast.error('Fehler beim PDF-Download');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDownloadEInvoice = async (invoiceId, invoiceNumber, e) => {
    if (e) e.stopPropagation();
    setDownloadingEInvoice(invoiceId);
    try {
      const response = await invoiceService.downloadEInvoice(invoiceId);
      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        const blob = await response.blob();
        const ext = contentType.includes('xml') ? 'xml' : 'pdf';
        triggerDownload(blob, `E-Rechnung_${invoiceNumber || invoiceId}.${ext}`);
        toast.success('E-Rechnung heruntergeladen');
      } else {
        const json = await response.json().catch(() => null);
        toast.error(json?.detail || 'E-Rechnung ist über die API nicht verfügbar. Bitte in Zoho Books herunterladen.', { duration: 6000 });
      }
    } catch (err) {
      toast.error('Fehler beim E-Rechnungs-Download');
    } finally {
      setDownloadingEInvoice(null);
    }
  };

  const closeDetail = () => {
    setShowDetail(false);
    setInvoiceDetail(null);
  };

  const filteredInvoices = invoices.filter(invoice => {
    const term = searchTerm.toLowerCase();
    return (
      (invoice.invoice_number?.toLowerCase() || '').includes(term) ||
      (invoice.customer_name?.toLowerCase() || '').includes(term) ||
      (invoice.reference_number?.toLowerCase() || '').includes(term)
    );
  });

  const filteredCreditNotes = creditNotes.filter(cn => {
    const term = searchTerm.toLowerCase();
    return (
      (cn.creditnote_number?.toLowerCase() || '').includes(term) ||
      (cn.customer_name?.toLowerCase() || '').includes(term)
    );
  });

  const getStatusBadge = (status) => {
    const variants = {
      paid: { label: 'Bezahlt', color: 'bg-green-50 text-green-700 border-green-200' },
      sent: { label: 'Versendet', color: 'bg-blue-50 text-blue-700 border-blue-200' },
      draft: { label: 'Entwurf', color: 'bg-slate-50 text-slate-700 border-slate-200' },
      overdue: { label: 'Überfällig', color: 'bg-red-50 text-red-700 border-red-200' },
      partial: { label: 'Teilbezahlt', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
      void: { label: 'Storniert', color: 'bg-slate-100 text-slate-500 border-slate-300' },
    };
    const config = variants[status] || variants.draft;
    return <Badge className={`${config.color} border`}>{config.label}</Badge>;
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('de-DE'); }
    catch { return dateStr; }
  };

  const isPaid = (status) => status === 'paid';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="invoices-page"
    >
      {/* Page Header */}
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
            Rechnungen & Gutschriften
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Ihre Rechnungen und Gutschriften aus Zoho Books
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchInvoices} disabled={loading} data-testid="refresh-invoices">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Source Info */}
      {source && (
        <div className={`mb-6 p-4 rounded-lg border flex items-center gap-3 ${
          source === 'zoho_books' ? 'bg-green-50 border-green-200'
            : error ? 'bg-yellow-50 border-yellow-200'
            : 'bg-blue-50 border-blue-200'
        }`} data-testid="data-source-info">
          {source === 'zoho_books' ? (
            <>
              <Cloud className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">Live-Daten von Zoho Books</p>
                <p className="text-xs text-green-600">{totalCount} Rechnungen geladen</p>
              </div>
            </>
          ) : (
            <>
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-800">{error || 'Zoho Books nicht verbunden'}</p>
                <p className="text-xs text-yellow-600">Bitte konfigurieren Sie die Zoho Books Verbindung im Admin-Bereich</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="search"
                placeholder="Rechnungsnummer oder Kunde suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="invoice-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="paid">Bezahlt</SelectItem>
                <SelectItem value="sent">Versendet</SelectItem>
                <SelectItem value="draft">Entwurf</SelectItem>
                <SelectItem value="overdue">Überfällig</SelectItem>
                <SelectItem value="partially_paid">Teilbezahlt</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Rechnungen & Gutschriften */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 border border-slate-200 p-1">
          <TabsTrigger value="invoices" data-testid="invoices-tab" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <FileText className="w-4 h-4 mr-2" />
            Rechnungen ({filteredInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="creditnotes" data-testid="creditnotes-tab" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <CreditCard className="w-4 h-4 mr-2" />
            Gutschriften ({filteredCreditNotes.length})
          </TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardContent className="pt-6">
              {filteredInvoices.length === 0 ? (
                <div className="text-center py-12" data-testid="no-invoices-message">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-600 mb-2">Keine Rechnungen gefunden</h3>
                  <p className="text-sm text-slate-500">
                    {error || (source === 'zoho_books'
                      ? 'Es wurden keine Rechnungen in Zoho Books gefunden.'
                      : 'Bitte konfigurieren Sie die Zoho Books Verbindung.')}
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg border-slate-200 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 border-b border-slate-200">
                        <TableHead className="font-medium text-slate-600">Rechnungsnr.</TableHead>
                        <TableHead className="font-medium text-slate-600">Kunde</TableHead>
                        <TableHead className="font-medium text-slate-600">Datum</TableHead>
                        <TableHead className="font-medium text-slate-600">Fällig</TableHead>
                        <TableHead className="font-medium text-slate-600">Betrag</TableHead>
                        <TableHead className="font-medium text-slate-600">Status</TableHead>
                        <TableHead className="font-medium text-slate-600 text-right">Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvoices.map((invoice) => (
                        <TableRow
                          key={invoice.invoice_id}
                          className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors cursor-pointer"
                          data-testid={`invoice-row-${invoice.invoice_number}`}
                          onClick={() => openInvoiceDetail(invoice)}
                        >
                          <TableCell className="font-medium text-slate-900">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-blue-600" />
                              {invoice.invoice_number || '-'}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-700">{invoice.customer_name || '-'}</TableCell>
                          <TableCell className="text-slate-600">{formatDate(invoice.invoice_date)}</TableCell>
                          <TableCell className="text-slate-600">{formatDate(invoice.due_date)}</TableCell>
                          <TableCell className="font-semibold text-slate-900">{formatCurrency(invoice.total)}</TableCell>
                          <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {isPaid(invoice.status) && (
                                <>
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={(e) => handleDownloadPdf(invoice.invoice_id, invoice.invoice_number, e)}
                                    disabled={downloadingPdf === invoice.invoice_id}
                                    data-testid={`download-pdf-${invoice.invoice_number}`}
                                    title="PDF herunterladen"
                                  >
                                    {downloadingPdf === invoice.invoice_id
                                      ? <Loader2 className="w-4 h-4 animate-spin" />
                                      : <Download className="w-4 h-4" />}
                                  </Button>
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={(e) => handleDownloadEInvoice(invoice.invoice_id, invoice.invoice_number, e)}
                                    disabled={downloadingEInvoice === invoice.invoice_id}
                                    data-testid={`download-einvoice-${invoice.invoice_number}`}
                                    title="E-Rechnung herunterladen"
                                  >
                                    {downloadingEInvoice === invoice.invoice_id
                                      ? <Loader2 className="w-4 h-4 animate-spin" />
                                      : <FileDown className="w-4 h-4" />}
                                  </Button>
                                </>
                              )}
                              <Button variant="ghost" size="sm"
                                onClick={(e) => { e.stopPropagation(); openInvoiceDetail(invoice); }}
                                data-testid={`view-invoice-${invoice.invoice_number}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Credit Notes Tab */}
        <TabsContent value="creditnotes">
          <Card style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CardContent className="pt-6">
              {filteredCreditNotes.length === 0 ? (
                <div className="text-center py-12" data-testid="no-creditnotes-message">
                  <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-600 mb-2">Keine Gutschriften gefunden</h3>
                  <p className="text-sm text-slate-500">Es wurden keine Gutschriften in Zoho Books gefunden.</p>
                </div>
              ) : (
                <div className="border rounded-lg border-slate-200 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 border-b border-slate-200">
                        <TableHead className="font-medium text-slate-600">Gutschriftnr.</TableHead>
                        <TableHead className="font-medium text-slate-600">Kunde</TableHead>
                        <TableHead className="font-medium text-slate-600">Datum</TableHead>
                        <TableHead className="font-medium text-slate-600">Betrag</TableHead>
                        <TableHead className="font-medium text-slate-600">Restbetrag</TableHead>
                        <TableHead className="font-medium text-slate-600">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCreditNotes.map((cn) => (
                        <TableRow
                          key={cn.creditnote_id}
                          className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                          data-testid={`creditnote-row-${cn.creditnote_number}`}
                        >
                          <TableCell className="font-medium text-slate-900">
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-green-600" />
                              {cn.creditnote_number || '-'}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-700">{cn.customer_name || '-'}</TableCell>
                          <TableCell className="text-slate-600">{formatDate(cn.date)}</TableCell>
                          <TableCell className="font-semibold text-green-700">{formatCurrency(cn.total)}</TableCell>
                          <TableCell className="text-slate-600">{formatCurrency(cn.balance)}</TableCell>
                          <TableCell>
                            <Badge className={cn.status === 'closed'
                              ? 'bg-green-50 text-green-700 border-green-200 border'
                              : 'bg-blue-50 text-blue-700 border-blue-200 border'}>
                              {cn.status === 'closed' ? 'Abgeschlossen' : cn.status === 'open' ? 'Offen' : cn.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invoice Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={closeDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <FileText className="w-5 h-5 text-blue-600" />
              Rechnung {invoiceDetail?.invoice_number}
            </DialogTitle>
          </DialogHeader>

          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : invoiceDetail ? (
            <div className="space-y-6">
              {/* Status & Amount */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Gesamtbetrag</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(invoiceDetail.total)}</p>
                  {invoiceDetail.balance > 0 && (
                    <p className="text-sm text-orange-600 mt-1">Offener Betrag: {formatCurrency(invoiceDetail.balance)}</p>
                  )}
                </div>
                <div className="text-right">
                  {getStatusBadge(invoiceDetail.status)}
                  {invoiceDetail.status_original && invoiceDetail.status_original !== invoiceDetail.status && (
                    <p className="text-xs text-slate-500 mt-1">Zoho: {invoiceDetail.status_original}</p>
                  )}
                </div>
              </div>

              {/* Download buttons for paid invoices */}
              {isPaid(invoiceDetail.status) && (
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleDownloadPdf(invoiceDetail.invoice_id, invoiceDetail.invoice_number)}
                    disabled={downloadingPdf === invoiceDetail.invoice_id}
                    data-testid="detail-download-pdf"
                  >
                    {downloadingPdf === invoiceDetail.invoice_id
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Download className="w-4 h-4 mr-2" />}
                    PDF herunterladen
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleDownloadEInvoice(invoiceDetail.invoice_id, invoiceDetail.invoice_number)}
                    disabled={downloadingEInvoice === invoiceDetail.invoice_id}
                    data-testid="detail-download-einvoice"
                  >
                    {downloadingEInvoice === invoiceDetail.invoice_id
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <FileDown className="w-4 h-4 mr-2" />}
                    E-Rechnung herunterladen
                  </Button>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Kunde</label>
                  <p className="mt-1 text-slate-900">{invoiceDetail.customer_name || '-'}</p>
                </div>
                {invoiceDetail.reference_number && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Referenznr.</label>
                    <p className="mt-1 text-slate-900">{invoiceDetail.reference_number}</p>
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Rechnungsdatum
                  </label>
                  <p className="mt-1 text-slate-900">{formatDate(invoiceDetail.invoice_date)}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Fälligkeitsdatum
                  </label>
                  <p className="mt-1 text-slate-900">{formatDate(invoiceDetail.due_date)}</p>
                </div>
              </div>

              {/* Amounts */}
              {(invoiceDetail.sub_total !== undefined || invoiceDetail.tax_total !== undefined) && (
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Beträge</h4>
                  <div className="space-y-2 text-sm">
                    {invoiceDetail.sub_total !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Zwischensumme</span>
                        <span className="text-slate-900">{formatCurrency(invoiceDetail.sub_total)}</span>
                      </div>
                    )}
                    {invoiceDetail.discount_total > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Rabatt</span>
                        <span className="text-green-700">-{formatCurrency(invoiceDetail.discount_total)}</span>
                      </div>
                    )}
                    {invoiceDetail.tax_total > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Steuern</span>
                        <span className="text-slate-900">{formatCurrency(invoiceDetail.tax_total)}</span>
                      </div>
                    )}
                    {invoiceDetail.adjustment !== undefined && invoiceDetail.adjustment !== 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Anpassung</span>
                        <span className="text-slate-900">{formatCurrency(invoiceDetail.adjustment)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t border-slate-200 pt-2">
                      <span className="text-slate-900">Gesamt</span>
                      <span className="text-slate-900">{formatCurrency(invoiceDetail.total)}</span>
                    </div>
                    {invoiceDetail.payment_made > 0 && (
                      <div className="flex justify-between text-green-700">
                        <span>Bezahlt</span>
                        <span>{formatCurrency(invoiceDetail.payment_made)}</span>
                      </div>
                    )}
                    {invoiceDetail.balance > 0 && (
                      <div className="flex justify-between font-semibold text-orange-700">
                        <span>Offen</span>
                        <span>{formatCurrency(invoiceDetail.balance)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Line Items */}
              {invoiceDetail.line_items && invoiceDetail.line_items.length > 0 && (
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Positionen</h4>
                  <div className="space-y-2">
                    {invoiceDetail.line_items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">{item.item_name || item.description || 'Artikel'}</p>
                          <p className="text-sm text-slate-500">
                            {item.quantity}x {formatCurrency(item.unit_price)}
                            {item.item_sku && <span className="ml-2 text-xs bg-slate-200 px-1.5 py-0.5 rounded">SKU: {item.item_sku}</span>}
                          </p>
                        </div>
                        <p className="font-semibold text-slate-900">{formatCurrency(item.total)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Billing Address */}
              {invoiceDetail.billing_address && invoiceDetail.billing_address.street && (
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                    <Building className="w-4 h-4" /> Rechnungsadresse
                  </h4>
                  <div className="text-slate-600 text-sm">
                    {invoiceDetail.billing_address.street && <p>{invoiceDetail.billing_address.street}</p>}
                    <p>{[invoiceDetail.billing_address.zip, invoiceDetail.billing_address.city].filter(Boolean).join(' ')}</p>
                    {invoiceDetail.billing_address.country && <p>{invoiceDetail.billing_address.country}</p>}
                  </div>
                </div>
              )}

              {/* Notes */}
              {invoiceDetail.notes && (
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Anmerkungen</h4>
                  <p className="text-sm text-slate-600">{invoiceDetail.notes}</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
